import React from "react";
import { defaultSettings } from './MentionsOverTimeSettings';
import MentionsOverTimeDisplay from "./MentionsOverTimeDisplay";
import i18n from 'translation';
import withQueryWidget, { WithQueryWidgetProps } from "components/widgetParts/queryWidgetComposition/withQueryWidget";
import { getSingleWidgetResponseByName } from "system/widget/Widget";

export interface Frequency {
  label: string;
  unit: string;
  value: number;
}

// Seconds per unit, matching the thresholds used by auto mode
const unitSeconds: Record<string, number> = {
  second: 1,
  minute: 60,
  hour: 3600,
  day: 86400,
  week: 604800,
  month: 2419200,
  year: 29030400,
};

// Get the lowest time unit for the frequency, or the forced unit when not 'auto'
function getFrequency(mentionsPerSecond: number, title: string, forcedUnit: string = 'auto') {
  // Auto: first unit (smallest to largest) where at least one mention occurs per unit; year is the fallback
  const unit = unitSeconds[forcedUnit]
    ? forcedUnit
    : Object.keys(unitSeconds).find(key => mentionsPerSecond > 1 / unitSeconds[key]) ?? 'year';

  const frequency = mentionsPerSecond * unitSeconds[unit];
  const label = `${i18n.t(unit)}`;

  return {
    label: `${i18n.t("mentions-per-example", { category: title, unit: label})}`,
    unit: label,
    value: frequency
  };
}

// Get the amount of records of selected category and the title
function getRecordsAmount(response: any): { amount: number, title: string } {
  if (!response) {
    return { amount: 0, title: `${i18n.t("widget-types.mentions-over-time.mentions")}`};
  }

  const keys = Object.keys(response);

  if (keys.length === 1 && keys[0] === '_total') {
    return { amount: response['_total'], title: `${i18n.t("widget-types.mentions-over-time.mentions")}` };
  }

  if (keys.length > 1) {
    const category = keys.find(key => key !== '_total');
    if (category) {
      return { amount: response[category], title: category };
    }
  }

  return { amount: 0, title: `${i18n.t("widget-types.mentions-over-time.mentions")}` };
}

function MentionsOverTime(props: WithQueryWidgetProps): React.JSX.Element {
  const currentState = { ...defaultSettings, ...props.widget?.data?.settings };

  // Get the mentions per second and the title
  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'mentions-over-time');
  const rangesResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'mentions-over-time-ranges');

  // Get the records amount and title
  const { amount, title } = getRecordsAmount(apiResponseInfo?.responseBody || []);

  const rangesResponseBody = rangesResponseInfo?.responseBody?.[0] || [];

  // Get the lower and upper date of the query in milliseconds
  const lowerDate = new Date(rangesResponseBody.lower).getTime();
  const upperDate = new Date(rangesResponseBody.upper).getTime();

  // Calculate amount of mentions per second
  const timespan = (upperDate - lowerDate) / 1000;
  const mentionsPerSecond = amount / timespan;

  const mentionsFrequency = getFrequency(mentionsPerSecond, title, currentState.frequencyUnit);

  return (
    <MentionsOverTimeDisplay
      frequency={mentionsFrequency}
      title={mentionsFrequency.label}
      widgetQueryTitle={apiResponseInfo?.queryDisplayTitle}
      settingsState={currentState} />
  );
}

export default withQueryWidget(MentionsOverTime, {
  hasSettingsDialog: true,
  hasQueryDialog: true,
});
